import { useQuery } from "@tanstack/react-query";
import { DatePicker } from "./DatePicker";
import { TagPicker } from "./TagPicker";
import { meQuery } from "@/lib/api";
import { canComplete } from "@/lib/approval";
import { WorkflowStatusIcon } from "./WorkflowStatusIcon";
/** Property pickers bound to a task: status, priority, lead, members, milestone, parent, dates. */
import { useTranslation } from "react-i18next";
import { Flag, GitBranch, Users } from "lucide-react";
import { PRIORITIES } from "@/lib/issues";
import { liveMilestones, liveTasks, patchTask, type ProjectData, type Task } from "@/lib/store";
import { TASK_STATUSES, TASK_STATUS_COLOR, descendants, type TaskStatus } from "@/lib/tasks";
import { errorMessage } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Avatar, PriorityIcon } from "./PropertyIcons";
import { Picker, PropButton } from "./Picker";

type Ctl = { open?: boolean; onOpenChange?: (o: boolean) => void };

async function save(task: Task, patch: Parameters<typeof patchTask>[1]) {
  try {
    await patchTask(task, patch);
  } catch (e) {
    toast(errorMessage(e), { tone: "error" });
  }
}

export function TaskStatusIcon({ status, className }: { status: string; className?: string }) {
  return <WorkflowStatusIcon status={status} color={TASK_STATUS_COLOR[status as TaskStatus] ?? "#8b8d91"} className={className} />;
}

export function TaskStatusPicker({ task, compact, ...ctl }: { task: Task; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  const me = useQuery(meQuery).data?.user;
  return (
    <Picker
      {...ctl}
      value={task.status}
      items={TASK_STATUSES.filter((status) => status !== "completed" || canComplete(task, task.status, me?.id)).map((s) => ({ id: s, label: t(`taskStatus.${s}`), icon: <TaskStatusIcon status={s} /> }))}
      onSelect={(id) => void save(task, { status: id })}
      trigger={
        <PropButton title={t("issue.status")} className={compact ? "w-6 justify-center px-0" : ""}>
          <TaskStatusIcon status={task.status} />
          {!compact && <span>{t(`taskStatus.${task.status}`)}</span>}
        </PropButton>
      }
    />
  );
}

export function TaskPriorityPicker({ task, compact, ...ctl }: { task: Task; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  return (
    <Picker
      {...ctl}
      value={String(task.priority)}
      items={PRIORITIES.map((p) => ({ id: String(p.value), label: t(`priority.${p.key}`), icon: <PriorityIcon priority={p.value} /> }))}
      onSelect={(id) => void save(task, { priority: Number(id) })}
      trigger={
        <PropButton title={t("issue.priority")} className={compact ? "w-6 justify-center px-0" : ""}>
          <PriorityIcon priority={task.priority} />
          {!compact && <span>{t(`priority.${PRIORITIES[task.priority]?.key ?? "none"}`)}</span>}
        </PropButton>
      }
    />
  );
}

export function TaskLeadPicker({ d, task, compact, ...ctl }: { d: ProjectData; task: Task; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  const user = task.lead_id ? d.users[task.lead_id] : undefined;
  return (
    <Picker
      {...ctl}
      value={task.lead_id ?? "none"}
      placeholder={t("task.setLead")}
      items={[{ id: "none", label: t("task.noLead"), icon: <Avatar size={16} /> }, ...Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} />, keywords: u.email }))]}
      onSelect={(id) => void save(task, { lead_id: id === "none" ? null : id })}
      trigger={
        <PropButton title={t("task.lead")} className={compact ? "w-6 justify-center px-0" : ""}>
          <Avatar user={user} size={16} />
          {!compact && <span className="truncate">{user?.name ?? t("task.noLead")}</span>}
        </PropButton>
      }
    />
  );
}

export function TaskMembersPicker({ d, task }: { d: ProjectData; task: Task }) {
  const { t } = useTranslation();
  const members = task.member_ids.map((id) => d.users[id]).filter((u): u is NonNullable<typeof u> => !!u);
  return (
    <Picker
      multi
      value={task.member_ids}
      placeholder={t("task.addMember")}
      items={Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} />, keywords: u.email }))}
      onSelect={(id) => void save(task, { member_ids: task.member_ids.includes(id) ? task.member_ids.filter((x) => x !== id) : [...task.member_ids, id] })}
      trigger={
        <PropButton title={t("task.members")}>
          {members.length === 0 ? (
            <>
              <Users className="h-3.5 w-3.5" /> <span>{t("task.addMember")}</span>
            </>
          ) : (
            <span className="flex -space-x-1">
              {members.map((u) => <Avatar key={u.id} user={u} size={18} className="ring-1 ring-elevated" />)}
            </span>
          )}
        </PropButton>
      }
    />
  );
}

export function TaskMilestonePicker({ d, task, compact }: { d: ProjectData; task: Task; compact?: boolean }) {
  const { t } = useTranslation();
  const m = task.milestone_id ? d.milestones[task.milestone_id] : undefined;
  return (
    <Picker
      value={task.milestone_id ?? "none"}
      placeholder={t("task.setMilestone")}
      items={[{ id: "none", label: t("task.noMilestone") }, ...liveMilestones(d).map((ms) => ({ id: ms.id, label: ms.name, icon: <Flag className="h-3.5 w-3.5" />, hint: ms.target_date }))]}
      onSelect={(id) => void save(task, { milestone_id: id === "none" ? null : id })}
      trigger={
        <PropButton title={t("nav.milestone")} className={compact ? "px-1" : ""}>
          <Flag className="h-3.5 w-3.5" />
          {!compact && <span className="truncate">{m?.name ?? t("task.noMilestone")}</span>}
        </PropButton>
      }
    />
  );
}

export function TaskParentPicker({ d, task }: { d: ProjectData; task: Task }) {
  const { t } = useTranslation();
  const parent = task.parent_id ? d.tasks[task.parent_id] : undefined;
  const forbidden = new Set([task.id, ...descendants(d, task.id).map((x) => x.id)]);
  return (
    <Picker
      value={task.parent_id ?? "none"}
      placeholder={t("task.setParent")}
      items={[{ id: "none", label: t("task.noParent") }, ...liveTasks(d).filter((x) => !forbidden.has(x.id)).map((x) => ({ id: x.id, label: x.name, icon: <TaskStatusIcon status={x.status} className="h-3 w-3" /> }))]}
      onSelect={(id) => void save(task, { parent_id: id === "none" ? null : id })}
      trigger={
        <PropButton title={t("task.parent")}>
          <GitBranch className="h-3.5 w-3.5" />
          <span className="truncate">{parent?.name ?? t("task.noParent")}</span>
        </PropButton>
      }
    />
  );
}

export function TaskDateButton({ task, field, compact, disabled }: { task: Task; field: "start_date" | "target_date"; compact?: boolean; disabled?: boolean }) {
  const { t } = useTranslation();
  return <DatePicker value={task[field]} label={t(field === "start_date" ? "task.startDate" : "task.targetDate")} boundary={field === "start_date" ? "start" : "end"}
    min={field === "target_date" ? task.start_date : undefined} max={field === "start_date" ? task.target_date : undefined}
    compact={compact} disabled={disabled} onChange={(value) => patchTask(task, { [field]: value || null })} />;
}

export function TaskTagPicker({ d, task, disabled }: { d: ProjectData; task: Task; disabled?: boolean }) {
  return <TagPicker d={d} value={task.label_ids} onChange={(label_ids) => patchTask(task, { label_ids })} disabled={disabled} />;
}

export function ProgressRing({ ratio, size = 16, className = "" }: { ratio: number | null; size?: number; className?: string }) {
  const r = (size - 3) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={className} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
      {ratio != null && (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--accent)" strokeWidth="2" strokeDasharray={`${c * ratio} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="round" />
      )}
    </svg>
  );
}

export function HealthDot({ health, className = "" }: { health?: string; className?: string }) {
  const { t } = useTranslation();
  if (!health) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11.5px] ${className}`} title={t(`health.${health}`)}>
      <span className="h-2 w-2 rounded-full" style={{ background: { on_track: "#4cb782", at_risk: "#f2c94c", off_track: "#eb5757" }[health] ?? "#888" }} />
      {t(`health.${health}`)}
    </span>
  );
}
